import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getOrganizationDetails, lookupAccount } from '../api';
import { AuthStateContext } from '../auth/AuthProvider';
import { Account, Organization } from '../types/Account';
import log from '../utils/log';
import { AlertManager } from './AlertManager';

type LoadingState = 'initial' | 'loading' | 'complete' | 'error';

type AccountContextProps = {
  loading: boolean;
  /** more granular than loading, used for lazy fetch */
  loadingState: LoadingState;
  account?: Account;
  /** error from the most recent account lookup, for debugging */
  accountError?: Error;
  organization?: Organization;
  organizationError?: Error;
  refetch: () => Promise<void>;
  /** locally update the Organization  */
  setOrganization: (organization: Organization) => void;
  /** internal value for kicking of lazy fetch */
  _triggered: boolean;
  /** set internal value for kicking of lazy fetch */
  _setTriggered: (_triggered: boolean) => void;
};

export const AccountContext = React.createContext<AccountContextProps>({
  loadingState: 'initial',
  loading: true,
  setOrganization: () => Promise.reject(),
  refetch: () => Promise.reject(),
  _triggered: false,
  _setTriggered: () => {},
});

/**
 * Provides the signed-in user's `Account` (and `Organization`, for org admins).
 *
 * The lookup is lazy: nothing is fetched until a component calls
 * `useAccountContext()` while the user is signed in. Reading
 * `AccountContext` directly via `useContext` does not trigger it.
 * Signing out clears the data so it's re-fetched on the next sign-in.
 */
export const AccountContextProvider: React.FC = ({ children }) => {
  const { authState } = useContext(AuthStateContext);
  const [_triggered, _setTriggered] = useState(false);

  const [loadingState, setLoadingState] = useState<LoadingState>('initial');

  // let's default to `loading` so we avoid things like
  // unnecessary auto-redirects on the EnterpriseAccountPage
  const [loading, setLoading] = useState(true);

  const [account, setAccount] = useState<Account>();
  const [accountError, setAccountError] = useState<Error>();
  const [organization, setOrganization] = useState<Organization>();

  // HACK - track this to avoid infinite loops
  const [organizationError, setOrganizationError] = useState<Error>();

  const { setAlert } = useContext(AlertManager);

  const fetch = useCallback(
    (silently: boolean = false) => {
      if (!silently) {
        setLoadingState('loading');
        setLoading(true);
      }
      setAccountError(undefined);
      return lookupAccount({ includePayments: true })
        .then((a) => {
          setAccount(a);

          if ('orgId' in a && !!a.orgId && a.orgRole === 'admin') {
            setOrganizationError(undefined);
            return getOrganizationDetails(a.orgId)
              .then(setOrganization)
              .catch((err) => {
                setOrganizationError(err);
                setAlert({
                  title: 'Organization error.',
                  message: err.message,
                  severity: 'error',
                });
              });
          }
        })
        .then(() => {
          setLoadingState('complete');
          setLoading(false);
        })
        .catch((err) => {
          log.warn('AccountContextProvider.fetch.catch');
          log.error(err);
          setAccountError(err);
          // a failed silent `refetch` keeps the previously loaded account
          if (!silently) {
            setLoadingState('error');
            setLoading(false);
          }
          setAlert({
            title: 'Something went wrong',
            message: 'Unable to retrieve account information',
            severity: 'error',
          });
          throw err;
        });
    },
    [setAlert],
  );

  // handle auto-fetch & lazy load via `_triggered`
  useEffect(() => {
    switch (authState) {
      case 'loading': {
        setLoading(true);
        break;
      }
      case 'signIn': {
        setLoading(false);
        if (
          _triggered &&
          (loadingState === 'complete' || loadingState === 'error')
        ) {
          setLoadingState('initial');
          setAccount(undefined);
          setAccountError(undefined);
          setOrganization(undefined);
        }
        break;
      }
      case 'signedIn': {
        if (_triggered && loadingState === 'initial') {
          setLoadingState('loading');
          // `fetch` already handles the error (alert + `accountError`), it
          // only re-throws for `refetch` callers
          fetch().catch(() => {});
        } else if (loadingState !== 'loading') {
          // `loadingState` is a dep, so this effect re-runs as soon as the
          // fetch above flips it to 'loading' - don't clear `loading` until
          // `fetch` finishes, or EnterpriseAccountPage sees no account and
          // redirects to /account, dropping the Stripe Checkout
          // `?checkout=success&session_id=` params (GFP-72)
          setLoading(false);
        }
        break;
      }
    }
  }, [authState, _triggered, fetch, loadingState]);

  return (
    <AccountContext.Provider
      value={{
        loading,
        loadingState,
        account,
        accountError,
        organization,
        organizationError,
        setOrganization,
        refetch: () => fetch(true),
        _triggered,
        _setTriggered,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountContext = () => {
  const context = useContext(AccountContext);

  const { _triggered, _setTriggered } = context;

  // wait till we ask for it to make the first request
  useEffect(() => {
    if (!_triggered) {
      _setTriggered(true);
    }
  }, [_triggered, _setTriggered]);

  return context;
};

// /**
//  * HOC to inject the `accountCtx` prop of type `AccountContextProps`
//  */
// export const withAccountContext = <
//   P extends { accountCtx: AccountContextProps }
// >(
//   Comp: ComponentType<P>
// ) => {
//   type WrappedProps = Omit<P, 'accountCtx'>;

//   const Wrapped: FunctionComponent<WrappedProps> = (props) => {
//     const accountCtx = useAccountContext();
//     const newProps = { ...props, accountCtx } as P;
//     // @ts-ignore - TODO - fix react types error
//     return <Comp {...newProps} />;
//   };

//   return Wrapped;
// };
